import { Router } from "@angular/router";
import { AsyncPipe } from "@angular/common";
import { MatListModule } from "@angular/material/list";
import { TranslateModule } from "@ngx-translate/core";
import { Component, ViewEncapsulation, inject } from "@angular/core";

import { SidebarItem } from "../../interfaces";
import { SidebarListItemComponent } from "../sidebar-list-item/sidebar-list-item.component";
import { ApplicationUserTypeName, UserRole } from "../../enums";
import { AuthService, SidebarToggleService } from "../../services";

import { Permissions } from "src/assets/config/config";
import { UsersService } from "src/app/features";
import { IsEnglishDirective } from "src/app/shared";

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [AsyncPipe, MatListModule, SidebarListItemComponent, IsEnglishDirective, TranslateModule],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent {
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #usersService = inject(UsersService);

  userInfo = this.#usersService.currentUser;
  isEnglish = false;

  readonly userRole = this.#authService.userRole;
  readonly isMembershipUser = this.#authService.isMembershipUser;

  isSidebarOpened$ = inject(SidebarToggleService).isSidebarOpened$;

  sidebarItems: SidebarItem[] = [
    {
      label: "MY_PROFILE.TITLE",
      icon: "db-icon-profile",
      routerLinkName: "/profile",
      permissions: Permissions.Profile,
    },
    {
      label: "SERVICES.TITLE",
      icon: "db-icon-service",
      routerLinkName: "/services",
      permissions: Permissions.Services,
    },
    {
      label: "TICKETS.MY_RESERVATIONS",
      icon: "db-icon-ticket",
      routerLinkName: "/my-tickets",
      permissions: Permissions.MyTickets,
    },
    {
      label: "TICKETS.MY_REQUESTS",
      icon: "db-icon-change-request",
      routerLinkName: "/my-requests",
      permissions: Permissions.MyRequests,
      basedOnCondition: () =>
        [ApplicationUserTypeName.Client, ApplicationUserTypeName.Membership, ApplicationUserTypeName.CompanyMembership].includes(
          this.#authService.userType()!,
        ),
    },
    {
      label: "FRIENDS_OF_OMAN_MUSEUM.EVALUATIONS_AND_INTERESTS",
      icon: "db-icon-star",
      routerLinkName: "/evaluations-interests",
      permissions: Permissions.EvaluationsInterests,
      basedOnCondition: () => this.#authService.isMembershipUser() || this.#authService.isCompanyMembershipUser(),
    },
    {
      label: "DASHBOARD_TITLE",
      icon: "db-icon-raci-matrix",
      routerLinkName: "/admin/dashboard",
      permissions: Permissions.Dashboard,
    },
    {
      label: "CALENDAR.TITLE",
      icon: "db-icon-calendar",
      routerLinkName: "/admin/calendar",
      permissions: Permissions.Calendar,
    },
    {
      label: "SERVICES.TITLE",
      icon: "db-icon-service",
      routerLinkName: "/admin/booking-services",
      permissions: Permissions.BookingServices,
    },
    {
      label: "TICKETS.RESERVATIONS",
      icon: "db-icon-reservation",
      routerLinkName: "/admin/reservations",
      permissions: Permissions.Reservations,
    },
    {
      label: "USERS.USERS_MANAGEMENT",
      icon: "db-icon-user",
      routerLinkName: "/admin/users-management",
      permissions: Permissions.UsersManagement,
    },
    {
      label: "SERVICES.SERVICES_MANAGEMENT",
      icon: "db-icon-services-management",
      routerLinkName: "/admin/services-management",
      permissions: Permissions.ServicesManagement,
    },
    {
      label: "REQUESTS.MANAGEMENT",
      icon: "db-icon-change-request",
      routerLinkName: "/admin/requests-management",
      permissions: Permissions.RequestsManagement,
    },
    {
      label: "LEARNING_CENTER.TITLE",
      icon: "db-icon-group",
      routerLinkName: "/admin/learning-center",
      permissions: Permissions.LearningCenter.filter((role) => role !== UserRole.LEARNINGCENTER),
    },
    {
      label: "EVENTS.TITLE",
      icon: "db-icon-calendar-event",
      routerLinkName: "/admin/exhibitions-events",
      permissions: Permissions.Events.filter((role) => role !== UserRole.ADMIN),
    },
    {
      label: "REQUESTS.SCHOOL_UNIVERSITY_REQUESTS",
      icon: "db-icon-change-request",
      routerLinkName: "/admin/school-university-requests",
      permissions: Permissions.LearningCenter.filter((role) => role !== UserRole.ADMIN),
    },
    {
      label: "FRIENDS_OF_OMAN_MUSEUM.FOOM",
      icon: "db-icon-museum-friend",
      routerLinkName: "/admin/friends-of-oman-privilages",
      permissions: Permissions.Memberships.filter((role) => role !== UserRole.MUSEUMFRIENDSDEPARTMENT),
    },
    {
      label: "FRIENDS_OF_OMAN_MUSEUM.MEMBERSHIPS",
      icon: "db-icon-membership",
      routerLinkName: "/admin/memberships",
      permissions: Permissions.Memberships.filter((role) => role !== UserRole.ADMIN),
    },
    {
      label: "FRIENDS_OF_OMAN_MUSEUM.SEND_INVITATION",
      icon: "db-icon-email",
      routerLinkName: "/admin/send-invitation",
      permissions: Permissions.Memberships.filter((role) => role !== UserRole.ADMIN),
    },
    {
      label: "FRIENDS_OF_OMAN_MUSEUM.TARGETED_MESSAGES",
      icon: "db-icon-message",
      routerLinkName: "/admin/targeted-messages",
      permissions: Permissions.Memberships.filter((role) => role !== UserRole.ADMIN),
    },
    {
      label: "FRIENDS_OF_OMAN_MUSEUM.SEND_EVALUATION_FORM",
      icon: "db-icon-email",
      routerLinkName: "/admin/send-evaluation-form",
      permissions: Permissions.Memberships.filter((role) => role !== UserRole.ADMIN),
    },
    {
      label: "AUDIT_MONITOR.TITLE",
      icon: "db-icon-monitor",
      routerLinkName: "/admin/audit-monitors",
      permissions: Permissions.Audit,
    },
    {
      label: "REPORTS.TITLE",
      icon: "db-icon-sheet",
      routerLinkName: "/admin/reports",
      permissions: Permissions.Reports,
    },
  ];

  logoutItem: SidebarItem = {
    label: "AUTH.LOGOUT",
    routerLinkName: "/auth",
    icon: "db-icon-logout",
  };

  readonly isGuest = this.#authService.isGuest;

  logOut() {
    this.#authService.logOut();
    this.#router.navigate([`${this.isGuest() ? "/auth" : "/"}`]);
  }
}
